import React, { useState, forwardRef } from 'react';
import { EducationInfoWithId, Qualification } from '../../types/forms';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Alert,
  Divider,
  Paper,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

interface EducationTabProps {
  formData: EducationInfoWithId;
  onChange: (field: keyof EducationInfoWithId, value: any) => void;
  onNext: () => void;
  onBack: () => void;
  onSave: () => void;
  saveStatus: 'saved' | 'error' | null;
}

type FormErrors = {
  [key: string]: string;
};

const EDUCATION_FIELDS = [
  'courseName',
  'learningInstitution',
  'qualificationType',
  'hoursCompleted',
  'inProgress',
  'expectedCompletionDate',
  'completionDate',
  'awaitingGrade',
  'grade',
  'qualifications'
] as const;

type EducationField = typeof EDUCATION_FIELDS[number];

const EducationTab = forwardRef<HTMLDivElement, EducationTabProps>((props, ref) => {
  const { formData, onChange, onNext, onBack, onSave, saveStatus } = props;

  const [errors, setErrors] = useState<FormErrors>({});

  const validateEducationField = (field: EducationField, value: any): string => {
    switch (field) {
      case 'courseName':
        return !value ? 'Course name is required' : '';
      case 'learningInstitution':
        return !value ? 'Learning institution is required' : '';
      case 'qualificationType':
        return !value ? 'Qualification type is required' : '';
      case 'hoursCompleted':
        return !value || isNaN(value) ? 'Hours completed must be a number' : '';
      case 'expectedCompletionDate':
        if (!value && formData.inProgress) return 'Expected completion date is required';
        const expectedDate = dayjs(value);
        if (!expectedDate.isValid()) {
          return 'Please enter a valid date';
        }
        // Check if the expected completion date is in the future
        if (expectedDate.isBefore(dayjs(), 'day')) {
          return 'Expected completion date must be in the future';
        }
        return '';
      case 'completionDate':
        if (!value && formData.inProgress === false) return 'Completion date is required';
        const completionDate = dayjs(value);
        if (!completionDate.isValid()) {
          return 'Please enter a valid date';
        }
        // Check if the completion date is in the past
        if (completionDate.isAfter(dayjs(), 'day')) {
          return 'Completion date must be in the past';
        }
        return '';
      default:
        return '';
    }
  };

  const validateQualificationField = (field: keyof Qualification, value: any, qualification: Qualification): string => {
    switch (field) {
      case 'name':
        return !value ? 'Course name is required' : '';
      case 'institution':
        return !value ? 'Institution name is required' : '';
      case 'qualification':
        return !value ? 'Qualification type is required' : '';
      case 'expectedCompletionDate':
        if (!value && qualification.inProgress) return 'Expected completion date is required';
        const expectedDate = dayjs(value);
        if (!expectedDate.isValid()) {
          return 'Please enter a valid date';
        }
        // Check if the expected completion date is in the future
        if (expectedDate.isBefore(dayjs(), 'day')) {
          return 'Expected completion date must be in the future';
        }
        return '';
      case 'completionDate':
        if (!value && !qualification.inProgress) return 'Completion date is required';
        const completionDate = dayjs(value);
        if (!completionDate.isValid()) {
          return 'Please enter a valid date';
        }
        // Check if the completion date is in the past
        if (completionDate.isAfter(dayjs(), 'day')) {
          return 'Completion date must be in the past';
        }
        return '';
      case 'grade':
        if (!value && !qualification.inProgress && !qualification.awaitingGrade) {
          return 'Grade is required';
        }
        return '';
      default:
        return '';
    }
  };

  const handleFieldChange = (field: EducationField, value: any) => {
    try {
      const error = validateEducationField(field, value);
      setErrors(prev => ({
        ...prev,
        [field]: error,
      }));
      onChange(field, value);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setErrors(prev => ({
        ...prev,
        [field]: errorMessage,
      }));
    }
  };

  const handleQualificationChange = (index: number, field: keyof Qualification, value: any) => {
    const updatedQualifications = [...(formData.qualifications || [])];
    updatedQualifications[index] = {
      ...updatedQualifications[index],
      [field]: value,
    };

    // Validate the qualification field
    const error = validateQualificationField(field, value, updatedQualifications[index]);
    if (error) {
      setErrors(prev => ({
        ...prev,
        [`qualification_${index}_${field}`]: error,
      }));
    } else {
      const { [`qualification_${index}_${field}`]: _, ...restErrors } = errors;
      setErrors(restErrors);
    }

    onChange('qualifications', updatedQualifications);
  };

  const handleAddQualification = () => {
    const newQualification: Qualification = {
      name: '',
      institution: '',
      qualification: '',
      completionDate: '',
      expectedCompletionDate: '',
      inProgress: false,
      awaitingGrade: false,
      grade: '',
      details: '',
      file: null,
    };
    const updatedQualifications = [...(formData.qualifications || []), newQualification];
    onChange('qualifications', updatedQualifications);
  };

  const handleRemoveQualification = (index: number) => {
    const updatedQualifications = [...(formData.qualifications || [])];
    updatedQualifications.splice(index, 1);
    onChange('qualifications', updatedQualifications);
  };

  return (
    <div ref={ref}>
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Current Course Details
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Course Name"
              value={formData.courseName || ''}
              onChange={(e) => handleFieldChange('courseName', e.target.value)}
              error={!!errors.courseName}
              helperText={errors.courseName || 'Enter the name of your counselling course'}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Learning Institution"
              value={formData.learningInstitution}
              onChange={(e) => handleFieldChange('learningInstitution', e.target.value)}
              error={!!errors.learningInstitution}
              helperText={errors.learningInstitution || 'Enter the name of your learning institution'}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Qualification Type"
              value={formData.qualificationType || ''}
              onChange={(e) => handleFieldChange('qualificationType', e.target.value)}
              error={!!errors.qualificationType}
              helperText={errors.qualificationType || 'Enter your qualification type (e.g. Diploma, Certificate)'}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Hours Completed"
              type="number"
              value={formData.hoursCompleted}
              onChange={(e) => handleFieldChange('hoursCompleted', e.target.value)}
              error={!!errors.hoursCompleted}
              helperText={errors.hoursCompleted || 'Enter the number of hours completed'}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.inProgress === true}
                  onChange={(e) => handleFieldChange('inProgress', e.target.checked)}
                  color="primary"
                />
              }
              label="I am still studying this course"
            />
          </Grid>

          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {formData.inProgress ? (
                <DatePicker
                  label="Expected Completion Date"
                  value={formData.expectedCompletionDate ? dayjs(formData.expectedCompletionDate) : null}
                  onChange={(date) => handleFieldChange('expectedCompletionDate', date?.format('YYYY-MM-DD'))}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errors.expectedCompletionDate,
                      helperText: errors.expectedCompletionDate || 'When do you expect to complete this course?'
                    }
                  }}
                />
              ) : (
                <DatePicker
                  label="Completion Date"
                  value={formData.completionDate ? dayjs(formData.completionDate) : null}
                  onChange={(date) => handleFieldChange('completionDate', date?.format('YYYY-MM-DD'))}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errors.completionDate,
                      helperText: errors.completionDate || 'When did you complete this course?'
                    }
                  }}
                />
              )}
            </LocalizationProvider>
          </Grid>

          {!formData.inProgress && (
            <>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.awaitingGrade}
                      onChange={(e) => handleFieldChange('awaitingGrade', e.target.checked)}
                    />
                  }
                  label="Awaiting grading"
                />
              </Grid>

              {!formData.awaitingGrade && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Grade"
                    value={formData.grade || ''}
                    onChange={(e) => handleFieldChange('grade', e.target.value)}
                    error={!!errors.grade}
                    helperText={errors.grade}
                  />
                </Grid>
              )}
            </>
          )}

          {/* Supervisor name, supervisor phone number, and BACP number fields moved to LegalTab */}

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Additional Qualifications
            </Typography>
          </Grid>

          {(formData.qualifications || []).map((qualification, index) => (
            <Grid item xs={12} key={index}>
              <Paper elevation={2} sx={{ p: 2, position: 'relative' }}>
                <IconButton
                  onClick={() => handleRemoveQualification(index)}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <DeleteIcon />
                </IconButton>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={qualification.inProgress}
                          onChange={(e) => handleQualificationChange(index, 'inProgress', e.target.checked)}
                        />
                      }
                      label="Currently studying"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Course Name"
                      value={qualification.name}
                      onChange={(e) => handleQualificationChange(index, 'name', e.target.value)}
                      error={!!errors[`qualification_${index}_name`]}
                      helperText={errors[`qualification_${index}_name`]}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Institution"
                      value={qualification.institution}
                      onChange={(e) => handleQualificationChange(index, 'institution', e.target.value)}
                      error={!!errors[`qualification_${index}_institution`]}
                      helperText={errors[`qualification_${index}_institution`]}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Qualification Type"
                      value={qualification.qualification}
                      onChange={(e) => handleQualificationChange(index, 'qualification', e.target.value)}
                      error={!!errors[`qualification_${index}_qualification`]}
                      helperText={errors[`qualification_${index}_qualification`]}
                    />
                  </Grid>

                  {qualification.inProgress ? (
                    <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Expected Completion Date"
                          value={qualification.expectedCompletionDate ? dayjs(qualification.expectedCompletionDate) : null}
                          onChange={(date) => handleQualificationChange(index, 'expectedCompletionDate', date?.format('YYYY-MM-DD'))}
                          format="DD/MM/YYYY"
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              required: true,
                              error: !!errors[`qualification_${index}_expectedCompletionDate`],
                              helperText: errors[`qualification_${index}_expectedCompletionDate`]
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  ) : (
                    <>
                      <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Completion Date"
                            value={qualification.completionDate ? dayjs(qualification.completionDate) : null}
                            onChange={(date) => handleQualificationChange(index, 'completionDate', date?.format('YYYY-MM-DD'))}
                            format="DD/MM/YYYY"
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                error: !!errors[`qualification_${index}_completionDate`],
                                helperText: errors[`qualification_${index}_completionDate`]
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={qualification.awaitingGrade}
                              onChange={(e) => handleQualificationChange(index, 'awaitingGrade', e.target.checked)}
                            />
                          }
                          label="Awaiting grading"
                        />
                      </Grid>

                      {!qualification.awaitingGrade && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Grade"
                            value={qualification.grade || ''}
                            onChange={(e) => handleQualificationChange(index, 'grade', e.target.value)}
                            error={!!errors[`qualification_${index}_grade`]}
                            helperText={errors[`qualification_${index}_grade`]}
                          />
                        </Grid>
                      )}
                    </>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional Details"
                      value={qualification.details || ''}
                      onChange={(e) => handleQualificationChange(index, 'details', e.target.value)}
                      multiline
                      rows={2}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddQualification}
              fullWidth
            >
              Add Qualification
            </Button>
          </Grid>

          {/* Online Learning section moved to LegalTab */}

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button onClick={onBack} variant="outlined">
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={onSave}
              >
                Save Progress
              </Button>
              <Button variant="contained" color="primary" onClick={onNext}>
                Next
              </Button>
            </Box>
          </Grid>

          {saveStatus === 'saved' && (
            <Grid item xs={12}>
              <Alert severity="success">Progress saved successfully!</Alert>
            </Grid>
          )}
          
          {saveStatus === 'error' && (
            <Grid item xs={12}>
              <Alert severity="error">Error saving progress. Please try again.</Alert>
            </Grid>
          )}
        </Grid>
      </Box>
    </div>
  );
});

EducationTab.displayName = 'EducationTab';

export default EducationTab;